import { DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { SubscriptionManager } from '@tmx/core';
import {
  CsvImporterConfiguration,
  CsvImporterEvent,
  CsvImporterEventType,
  CsvImporterState,
  CsvImporterStatus,
  ErrorEvent,
  ErrorEventReasonCode,
  ImportCompleteEvent,
  ImportReadyStatusChangeEvent,
  InvalidStatusChangeEvent,
  StatusChangeEvent,
} from '@tmx/operandi';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
  validateLatitude,
  validateLatLon,
  validateLongitude,
  validateName,
} from 'src/app/auth/helpers/customValidators';
import { ModalboxComponent } from 'src/app/components/modalbox/modalbox.component';
import { RouteService } from 'src/app/services/route.service';
import { MAXDBLENGTHS } from 'src/app/utils/maxLengthConstant';
import { ErrorObject, SearchService } from 'src/app/services/search.service';
import { CompleteEvent } from 'src/app/_models/importer-events/complete-event';
import { EnableImportButtonEvent } from 'src/app/_models/importer-events/enable-import-button-event';
import { ImporterEvent } from 'src/app/_models/importer-events/importer-event';
import { LoadingEvent } from 'src/app/_models/importer-events/loading-event';
import { ShowBackButtonEvent } from 'src/app/_models/importer-events/show-back-button-event';
import {
  BatchLocationResponse,
  ErrorInformation,
  SingleSearchLocation,
} from 'src/app/_models/pcmws/singleSearch/singleSearchResponse';
import { StopLocation, StopState } from 'src/app/_models/state/stopState';
import { Route } from 'src/app/_models/view/Route';
import { UserService } from 'src/app/services/user.service';
import { RegionCode } from 'src/app/utils/regions';
import { EnableContinueButtonEvent } from 'src/app/_models/importer-events/continue-event';

export interface importData {
  row: number;
  match: string;
  inputAddress: string;
  matchAddress: string;
  inputLatLon: string;
  checked: boolean;
  displayText: string;
}

export const CHUNK_SIZE = 5000;
@Component({
  selector: 'app-import-stops',
  templateUrl: './import-stops.component.html',
  styleUrls: ['./import-stops.component.scss'],
})
export class ImportStopsComponent implements OnInit, OnDestroy {
  private sm = new SubscriptionManager();
  searchText = '';
  searchType = '';
  configuration: CsvImporterConfiguration = {
    persistentStorageKey: 'assetsImporter',
    downloadableCsvTemplateLink: {
      displayText: 'Download CSV Template',
      tooltipText: 'Sample Route Stops Upload.csv',
      url: '../assets/resources/importStops/sampleImportStops.csv',
    },
    fields: [
      {
        key: 'stop_name',
        label: 'Stop Name',
        required: false,
        validators: [],
        isMatch: (columnName) => {
          return columnName
            ? /stop_name/i.test(columnName.toLowerCase())
            : true;
        },
      },
      {
        key: 'latitude',
        label: 'Latitude',
        required: false,
        validators: [validateLatitude],
        isMatch: (columnName) =>
          columnName ? /latitude/i.test(columnName.toLowerCase()) : true,
      },
      {
        key: 'longitude',
        label: 'Longitude',
        required: false,
        validators: [validateLongitude],
        isMatch: (columnName) =>
          columnName ? /longitude/i.test(columnName.toLowerCase()) : true,
      },
      {
        key: 'coordinates',
        label: 'Coordinates',
        required: false,
        validators: [validateLatLon],
        isMatch: (columnName) =>
          columnName ? /coordinates/i.test(columnName?.toLowerCase()) : true,
      },
      {
        key: 'street_address',
        label: 'Street Address',
        required: false,
        isMatch: (columnName) =>
          columnName ? /street_address/i.test(columnName.toLowerCase()) : true,
      },
      {
        key: 'city',
        label: 'City',
        required: false,
        validators: [Validators.maxLength(MAXDBLENGTHS.CITY), validateName],
        isMatch: (columnName) =>
          columnName ? /city/i.test(columnName.toLowerCase()) : true,
      },
      {
        key: 'state',
        label: 'State',
        validators: [Validators.maxLength(MAXDBLENGTHS.STATE), validateName],
        required: false,
        isMatch: (columnName) =>
          columnName ? /state/i.test(columnName.toLowerCase()) : true,
      },
      {
        key: 'zip',
        label: 'Zip',
        required: false,
        validators: [Validators.maxLength(MAXDBLENGTHS.ZIP)],
        isMatch: (columnName) =>
          columnName ? /zip/i.test(columnName.toLowerCase()) : true,
      },
      {
        key: 'country',
        label: 'Country',
        required: false,
        validators: [Validators.maxLength(MAXDBLENGTHS.COUNTRY), validateName],
        isMatch: (columnName) =>
          columnName ? /country/i.test(columnName.toLowerCase()) : true,
      },
      {
        key: 'stop_duration',
        label: 'Stop Duration',
        required: false,
        isMatch: (columnName) =>
          columnName ? /stop_duration/i.test(columnName.toLowerCase()) : true,
      },
    ],
    // afterMappingAction: removeDuplicates,
  };
  @Input() hostEvent!: CsvImporterEvent | null;
  @Input() route: Route;
  @Output() eventChange = new EventEmitter<ImporterEvent>();
  succeeded = false;
  successMessage = '';
  errored = false;
  warningData = false;
  errorData = false;
  errorMessage = '';
  invalidRows: string[];
  showImportingSpinner = false;
  updatedDataVersion: string;
  unCertainRows: string[];
  hasEnableContinueButton = false;
  stopStates: StopState[];
  columns: importData[];
  isAllCheckBoxChecked = false;
  hasExactMatches = false;
  hasGoodMatches = false;
  hasUncertainMatches = false;
  hasFailedMatches = false;
  importStopsStatus = 'Importing';
  hasLatLon = false;
  importedDataHasAddress = false;
  importedDataHasLatLon = false;
  errorWhenProcessingRevGeo = false;

  constructor(
    private decimalPipe: DecimalPipe,
    private routeService: RouteService,
    private searchService: SearchService,
    private modalService: BsModalService,
    private userService: UserService
  ) {}
  ngOnInit() {
    this.sm.manage = this.routeService.selectedRoute$.subscribe(
      (routeState) => {
        this.updatedDataVersion = routeState?.historicalDataVersion;
      }
    );
    this.sm.manage = this.routeService.canAddImportStop$.subscribe((canAdd) => {
      if (canAdd) {
        this.addRoutesOnMap();
      } else {
        this.columns = [];
        this.stopStates = [];
        this.hasEnableContinueButton = false;
      }
    });
  }

  ngOnDestroy() {
    this.sm.unsubscribe();
  }

  handleEvent(event: CsvImporterEvent) {
    switch (event.type) {
      case CsvImporterEventType.StatusChange:
        this.handleStatusChangeEvent(event as StatusChangeEvent);
        break;
      case CsvImporterEventType.ImportComplete:
        this.handleImportCompleteEvent(event as ImportCompleteEvent);
        break;
      case CsvImporterEventType.Error:
        this.handleErrorEvent(event as ErrorEvent);
        break;
    }
  }

  private handleStatusChangeEvent(event: StatusChangeEvent) {
    const { state, status } = event;
    this.eventChange.next(
      new ShowBackButtonEvent(state !== CsvImporterState.Uploading)
    );

    if (state === CsvImporterState.Uploading) {
      this.resetAlerts();
      this.eventChange.next(new EnableImportButtonEvent(false));
      return;
    }

    if (state === CsvImporterState.Mapping) {
      if (status === CsvImporterStatus.Invalid) {
        const invalidEvent = event as InvalidStatusChangeEvent;
        const labels = invalidEvent.invalidRecords[0]
          .map((field) => field.label)
          .join(', ');
        this.showErrorAlert(
          `${labels} Still needs to be matched to your columns`
        );
      } else {
        this.resetAlerts();
      }
      this.eventChange.next(
        new EnableImportButtonEvent(
          status === CsvImporterStatus.ReadyToContinue
        )
      );
      return;
    }

    if (state === CsvImporterState.Validating) {
      this.eventChange.next(
        new EnableImportButtonEvent(
          status === CsvImporterStatus.ReadyToContinue
        )
      );
      if (status === CsvImporterStatus.ReadyToContinue) {
        this.eventChange.next(new LoadingEvent(false));
        this.showSuccessAlert(event as ImportReadyStatusChangeEvent);
      } else if (status === CsvImporterStatus.Invalid) {
        this.eventChange.next(new LoadingEvent(false));
        this.showErrorAlert(
          `Please resolve ${this.countErrors(
            event as InvalidStatusChangeEvent
          )} errors`
        );
      } else {
        this.eventChange.next(new LoadingEvent(true));
        this.resetAlerts();
      }
    }
  }

  private startsImportStosProcess(records) {
    this.showSpinner(true);
    this.eventChange.next(new LoadingEvent(true));
    const region = RegionCode[this.userService.getUser()?.region];
    const addressInfo: string[] | any[] = records.map((val) => {
      if (val?.latitude && val?.longitude) {
        this.hasLatLon = true;
        return [val?.longitude, val?.latitude].join(',');
      }

      if (val?.coordinates) {
        this.hasLatLon = true;
        const coords: string[] = val?.coordinates.split(',');
        return `${coords[1]}, ${coords[0]}`;
      }
      return {
        Address: {
          City: val?.city,
          Country: val?.country,
          State: val?.state,
          StreetAddress: val?.street_address,
          Zip: val?.zip,
        },
        region,
      };
    });
    this.importedDataHasAddress = records?.some(
      (val) =>
        val?.city ||
        val?.country ||
        val?.state ||
        val?.street_address ||
        val?.zip
    );
    this.importedDataHasLatLon = this.hasLatLon;
    if (this.hasLatLon) {
      this.processCoordinatesToGetAddress(records, addressInfo);
    } else {
      this.getBatchLocations(records, addressInfo);
    }
  }

  private async handleImportCompleteEvent({ records }: ImportCompleteEvent) {
    if (records?.length > 40) {
      this.eventChange.next(new EnableImportButtonEvent(false));
      this.showErrorAlert('Too many rows in the CSV file (Max 40)');
    } else {
      this.importStopsStatus = 'GeoCoding';
      if (!this.columns?.length) {
        this.startsImportStosProcess(records);
      }
    }
  }

  private getBatchLocations(records, addresses, errStops?: ErrorObject[]) {
    this.searchService
      .batchAddressSearch(addresses, this.updatedDataVersion)
      .subscribe(
        (addressResults: BatchLocationResponse[]) => {
          this.addStopLocations(records, addressResults, addresses, errStops);
        },
        (error) => {
          if (error?.err?.length) {
            const errDescription = error.err.map((err) => err.Description);
            this.showErrorAlert(errDescription);
          } else if (error?.length) {
            const errDescription = error?.find((err) => err?.Description);
            this.showErrorAlert(errDescription);
          } else {
            this.showErrorAlert('Invalid Records');
          }
          this.showSpinner(false);
          this.eventChange.next(new LoadingEvent(false));
        }
      );
  }

  private processCoordinatesToGetAddress(records, searchStrings: string[]) {
    this.eventChange.next(new LoadingEvent(true));
    this.sm.manage = this.searchService
      .bulkReverseGeoCode(searchStrings)
      .subscribe((results: (SingleSearchLocation | ErrorObject)[]) => {
        const addresses: Partial<SingleSearchLocation[]> = [];
        const errStops: ErrorObject[] = [];
        results.forEach((result) => {
          if (result['Address']) {
            const location: SingleSearchLocation =
              result as SingleSearchLocation;
            const address: Partial<SingleSearchLocation> = {
              Address: location?.Address,
              Region: location?.Region,
              Coords: location?.Coords,
            };
            addresses.push(address);
          } else {
            this.errorWhenProcessingRevGeo = true;
            const response = result as SingleSearchLocation;
            const err: ErrorInformation = response?.Errors?.length
              ? response.Errors[0]
              : null;
            errStops.push({
              index: response?.id,
              message: err?.Description ? err.Description : 'Invlalid Lat Lon',
            });
          }
        });
        if (!errStops?.length && addresses?.length) {
          this.getBatchLocations(records, addresses, errStops);
        }
        const tokenExpires = errStops.filter((err) => err?.status === 403);
        if (tokenExpires?.length > 0) {
          this.showErrorAlert(
            'Due to a technical issue, services are temporarily unavailable. Please check back again for an updated status. '
          );
        } else {
          this.setReverseGeoErrorAlerts(errStops);
          this.showSpinner(false);
          if (errStops?.length) {
            this.showErrorAlert('Invalid Lat Lon(s)');
          }

          this.eventChange.next(new LoadingEvent(false));
          this.eventChange.next(new EnableImportButtonEvent(false));
        }
      });
  }

  private addStopLocations(
    records,
    addressResults: BatchLocationResponse[],
    addresses: Partial<SingleSearchLocation[]>,
    errStops?: ErrorObject[]
  ) {
    if (addressResults?.length) {
      const errorData: ErrorObject[] = [];
      const stopStates: StopState[] = [];
      this.columns = addressResults.map((address, index) => {
        const inputAddress = [
          records[index]?.street_address,
          records[index]?.city,
          records[index]?.state,
          records[index]?.country,
          records[index]?.zip,
        ]
          .filter((val) => Boolean(val))
          .join(', ');
        const matchAddress = address?.Address
          ? Object.keys(address.Address)
              .map((key) => address.Address[key])
              .filter((val) => Boolean(val))
              .join(', ')
          : null;
        const latLon = records[index]?.coordinates
          ? ` ${records[index]?.coordinates} `
          : ` ${records[index]?.latitude},  ${records[index]?.longitude} `;

        return {
          checked: false,
          row: index + 1,
          match: address?.ConfidenceLevel ? address?.ConfidenceLevel : 'Failed',
          inputAddress: inputAddress,
          matchAddress: matchAddress,
          inputLatLon: latLon,
          displayText: [inputAddress, matchAddress, latLon]
            .filter((val) => Boolean(val))
            .join(' '),
        };
      });
      this.hasExactMatches = this.columns.some(
        (column) => column?.match?.toLowerCase() === 'exact'
      );
      this.hasGoodMatches = this.columns.some(
        (column) => column?.match?.toLowerCase() === 'good'
      );
      this.hasUncertainMatches = this.columns.some(
        (column) => column?.match?.toLowerCase() === 'uncertain'
      );
      this.hasFailedMatches = this.columns.some(
        (column) => column?.match?.toLowerCase() === 'failed'
      );
      addressResults.forEach((l, index) => {
        if (
          !(
            l?.ConfidenceLevel?.includes('Exact') ||
            l?.ConfidenceLevel?.includes('Uncertain')
          ) &&
          l?.Errors?.length > 0
        ) {
          const errorObj: ErrorObject = {
            index: index,
            searchString: l?.ConfidenceLevel,
            message: l?.Errors[0]?.Description,
          };
          errorData.push(errorObj);
        }
        const stop = l?.ConfidenceLevel ? l : addresses[index];
        const stopLocation = {
          name: records[index]?.stop_name,
          address: {
            streetAddress: stop?.Address?.StreetAddress,
            city: stop?.Address?.City,
            stateAbbreviation: stop?.Address?.StateAbbreviation,
            zip: stop?.Address?.Zip,
            county: stop?.Address?.County,
            countryAbbreviation: stop?.Address?.CountryAbbreviation,
          },
          coords: [
            Number.parseFloat(stop?.Coords?.Lon),
            Number.parseFloat(stop?.Coords?.Lat),
          ],
        } as StopLocation;

        const stopState = {
          location: stopLocation,
          stopTime: records[index]?.stop_duration,
          lngLat: [
            Number.parseFloat(stop?.Coords?.Lon),
            Number.parseFloat(stop?.Coords?.Lat),
          ],
        } as StopState;
        stopStates.push(stopState);
      });
      this.eventChange.next(new LoadingEvent(false));
      const errors = errStops?.length ? [...errorData, ...errStops] : errorData;
      this.setReverseGeoErrorAlerts(errors);
      this.hasEnableContinueButton = true;
      this.stopStates = stopStates;
      this.eventChange.next(new EnableContinueButtonEvent(true, stopStates));
      this.showSpinner(false);
      if (
        errors?.length &&
        !this.invalidRows?.length &&
        !this.unCertainRows?.length
      ) {
        this.setReverseGeoErrorAlerts(errors);
      }
    }
  }

  private addRoutesOnMap() {
    const indexes = this.columns.reduce(
      (result: number[], targetValue: importData, currentIndex: number) => {
        if (!targetValue.checked) {
          result.push(currentIndex);
        }
        return result;
      },
      []
    );
    this.stopStates = this.stopStates.filter((state, index) => {
      return indexes.some((idx) => idx === index);
    });
    const initialState = {
      title: 'Set Details',
      list: ['Append Stops to Route', 'Replace Stops in Route'],
      actionButtonText: 'Finish',
      btnClass: 'btn btn-primary',
    };
    const dialogRef = this.modalService.show<ModalboxComponent>(
      ModalboxComponent,
      {
        initialState,
        ignoreBackdropClick: true,
        keyboard: false,
        animated: true,
        class: 'modal-dialog-centered',
      }
    );
    this.sm.manage = dialogRef.content.event.subscribe(async (data) => {
      if (data === 0) {
        this.routeService.appendRouteStopStates(
          this.route?.id,
          this.stopStates
        );
      } else {
        this.routeService.updateRouteStopStates(
          this.route?.id,
          this.stopStates
        );
      }
      this.routeService.canImportStops(false);
      this.completeImport();
    });
  }

  private completeImport() {
    this.eventChange.next(new LoadingEvent(false));
    this.eventChange.next(new CompleteEvent());
  }

  private handleErrorEvent(event: ErrorEvent) {
    switch (event.reasonCode) {
      case ErrorEventReasonCode.SingleFileImportOnly:
        this.showErrorAlert(
          'Importing multiple files at the same time is not supported.'
        );
        break;
      case ErrorEventReasonCode.BadFileExtension:
        this.showErrorAlert('Only files in .csv and .txt format are accepted.');
        break;
      case ErrorEventReasonCode.ReadError:
        this.showErrorAlert(`Unable to read "${event.fileName}".`);
        break;
      case ErrorEventReasonCode.NoData:
      case ErrorEventReasonCode.NoDataAndHasParseErrors:
        this.showErrorAlert(`No CSV data in "${event.fileName}".`);
        break;
    }
  }

  private resetAlerts() {
    setTimeout(() => {
      this.succeeded = false;
      this.errored = false;
      this.errorMessage = '';
      this.warningData = false;
      this.errorData = false;
      this.invalidRows = [];
      this.unCertainRows = [];
      this.hasLatLon = false;
      this.importedDataHasAddress = false;
      this.importedDataHasLatLon = false;
      this.errorWhenProcessingRevGeo = false;
    });
  }

  private showSuccessAlert({
    imported,
    total,
    skipped,
  }: ImportReadyStatusChangeEvent) {
    setTimeout(() => {
      this.succeeded = true;
      this.successMessage = `Ready to import ${this.decimalPipe.transform(
        imported
      )} Stops`;
      if (skipped > 0) {
        this.successMessage += `. Skipped ${this.decimalPipe.transform(
          skipped
        )} duplicate stops out of ${this.decimalPipe.transform(total)} total`;
      }
      this.errored = false;
      this.warningData = false;
      this.errorData = false;
      this.errorMessage = '';
      this.invalidRows = [];
      this.unCertainRows = [];
    });
  }

  private showErrorAlert(message: string) {
    setTimeout(() => {
      this.succeeded = false;
      this.successMessage = '';
      this.errored = true;
      this.errorMessage = message;
    });
  }

  private setReverseGeoErrorAlerts(errors: ErrorObject[]) {
    this.unCertainRows = [];
    this.unCertainRows = errors
      .filter((error) => error.searchString === 'Uncertain')
      .map((error) => `Record: ${error.index + 1}, Warning: ${error.message}`);
    this.invalidRows = errors
      .filter((error) => error.searchString !== 'Uncertain')
      .map((error) => `Record: ${error.index + 1}, , Error: ${error.message}`);
    this.warningData = this.unCertainRows?.length > 0;
    this.errorData = this.invalidRows?.length > 0;
    this.succeeded = false;
  }

  private showSpinner(show: boolean) {
    setTimeout(() => {
      this.showImportingSpinner = show;
    });
  }

  private countErrors(invalidEvent: InvalidStatusChangeEvent) {
    let count = 0;
    for (const invalidRecord of invalidEvent.invalidRecords) {
      count += invalidRecord.length;
    }
    return count;
  }

  getStopsByMatchType(matchType: string) {
    this.isAllCheckBoxChecked = false;
    this.searchType = this.searchType === matchType ? '' : matchType;
  }

  handleCheckboxChange(ev, stop: importData) {
    const index = this.columns.findIndex((column) => column.row === stop.row);
    this.columns[index].checked = ev.target.checked;
    this.isAllCheckBoxChecked = this.columns.every((p) => p.checked);
  }

  skipAllCheckBox(ev: any) {
    this.columns
      .filter(
        (stop) =>
          stop?.checked ||
          (this.searchType?.length
            ? this.searchType.includes(stop?.match?.toLowerCase()) &&
              stop.displayText
                ?.toLowerCase()
                .includes(this.searchText?.toLowerCase())
            : stop.displayText
                ?.toLowerCase()
                .includes(this.searchText?.toLowerCase()))
      )
      .forEach((x) => (x.checked = ev.target.checked));
  }
}
